import type { FormField } from '@cli-types/core-engine/schema.js'
import { escapeAttribute, escapeJsx, jsString } from '@cli-utils/text.js'
import { renderFormPipelineFieldJsxRenderer } from '@next-adapter/templates/renderers/generators/form-pipeline/form-component-shared/generate-field-jsx.js'
import { errorMessageLine } from './error-message-line.js'
import { fieldContainerClass } from './field-container-class.js'
import { fieldDescriptionLine } from './field-description-line.js'
import { formLabelWithDescriptionLine } from './form-label-with-description-line.js'
import { generateFileUploadFieldJSX } from './generate-file-upload-field-jsx.js'
import { generateListFieldJSX } from './generate-list-field-jsx.js'
import { generateMultiselectFieldJSX } from './generate-multiselect-field-jsx.js'
import { generateNumberFieldJSX } from './generate-number-field-jsx.js'
import { generateTextFieldJSX } from './generate-text-field-jsx.js'
import { selectClass } from './select-class.js'
import { textareaClass } from './textarea-class.js'

export function generateFieldJSX(field: FormField): string {
  const name = field.name || ''
  const label = field.label ? escapeJsx(field.label) : ''
  const placeholder = field.placeholder || ''
  const hintJSX = fieldDescriptionLine(name, field.hint || '')
  const requiredStar = field.required ? ' <span className="text-destructive">*</span>' : ''

  switch (field.type) {
    case 'textarea':
      return renderFormPipelineFieldJsxRenderer({
        kind: 'renderFieldJsxFieldControllerTemplate',
        props: {
          name: name,
          fieldContainerClassFill: fieldContainerClass(
            field.height,
            field.height === 'fill' ? 'space-y-2 flex flex-col' : 'space-y-2'
          ),
          formLabelWithDescriptionLine: formLabelWithDescriptionLine(
            name,
            label,
            requiredStar,
            hintJSX,
            '              '
          ),
          placeholder: placeholder,
          textareaClass: textareaClass(field.height),
          errorMessageLine: errorMessageLine(jsString(name), '              ')
        }
      })

    case 'radio':
      if (field.options && field.options.length > 0) {
        const radioItems = field.options
          .map((opt) =>
            renderFormPipelineFieldJsxRenderer({
              kind: 'renderFieldJsxChangeHandlerTemplate',
              props: {
                escapeAttribute: escapeAttribute(`${name}-${opt.value}`),
                escapeAttributeText: escapeAttribute(opt.value),
                jsString: jsString(opt.value),
                escapeJsx: escapeJsx(opt.label)
              }
            })
          )
          .join('\n')
        return renderFormPipelineFieldJsxRenderer({
          kind: 'renderFieldJsxFieldControllerSpaceTemplate',
          props: {
            name: name,
            fieldContainerClass: fieldContainerClass(field.height, 'space-y-3'),
            formLabelWithDescriptionLine: formLabelWithDescriptionLine(
              name,
              label,
              requiredStar,
              hintJSX,
              '              '
            ),
            radioItems: radioItems,
            errorMessageLine: errorMessageLine(jsString(name), '              ')
          }
        })
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'select':
      if (field.options && field.options.length > 0) {
        const optionItems = field.options
          .map(
            (opt) =>
              `                <option value="${escapeAttribute(opt.value)}">${escapeJsx(opt.label)}</option>`
          )
          .join('\n')
        return renderFormPipelineFieldJsxRenderer({
          kind: 'renderFieldJsxFieldControllerEscapedJsxTemplate',
          props: {
            name: name,
            fieldContainerClass: fieldContainerClass(field.height),
            formLabelWithDescriptionLine: formLabelWithDescriptionLine(
              name,
              label,
              requiredStar,
              hintJSX,
              '              '
            ),
            selectClass: selectClass(field.height),
            escapeJsx: escapeJsx(placeholder || 'Select...'),
            optionItems: optionItems,
            errorMessageLine: errorMessageLine(jsString(name), '              ')
          }
        })
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'multiselect':
      return generateMultiselectFieldJSX(field, name, label, hintJSX, requiredStar)

    case 'checkbox':
      if (field.options && field.options.length > 0) {
        const checkboxItems = field.options
          .map((opt) =>
            renderFormPipelineFieldJsxRenderer({
              kind: 'renderFieldJsxChangeHandlerEscapedJsxTemplate',
              props: {
                escapeAttribute: escapeAttribute(`${name}-${opt.value}`),
                jsString: jsString(opt.value),
                escapeJsx: escapeJsx(opt.label)
              }
            })
          )
          .join('\n')
        return renderFormPipelineFieldJsxRenderer({
          kind: 'renderFieldJsxFieldControllerSpaceBranchTemplate',
          props: {
            name: name,
            fieldContainerClass: fieldContainerClass(field.height, 'space-y-3'),
            formLabelWithDescriptionLine: formLabelWithDescriptionLine(
              name,
              label,
              requiredStar,
              hintJSX,
              '              '
            ),
            checkboxItems: checkboxItems,
            errorMessageLine: errorMessageLine(jsString(name), '              ')
          }
        })
      }
      return renderFormPipelineFieldJsxRenderer({
        kind: 'renderFieldJsxFieldControllerFlexTemplate',
        props: {
          name: name,
          fieldContainerClass: fieldContainerClass(field.height, 'flex flex-row items-start gap-3'),
          formLabelWithDescriptionLine: formLabelWithDescriptionLine(
            name,
            label || name,
            requiredStar,
            hintJSX,
            '                '
          ),
          errorMessageLine: errorMessageLine(jsString(name), '                ')
        }
      })

    case 'email':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'email@example.com',
        hintJSX,
        requiredStar,
        'email',
        field.height
      )

    case 'number':
      return generateNumberFieldJSX(name, label, placeholder, hintJSX, requiredStar, field.height)

    case 'date':
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'date',
        field.height
      )

    case 'url':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'https://',
        hintJSX,
        requiredStar,
        'url',
        field.height
      )

    case 'phone':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || '+1 (555) 000-0000',
        hintJSX,
        requiredStar,
        'tel',
        field.height
      )

    case 'file':
    case 'upload':
      return generateFileUploadFieldJSX(field, name, label, hintJSX, requiredStar)

    case 'list':
      return generateListFieldJSX(field, name, label, hintJSX, requiredStar)

    default:
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )
  }
}
